import {
  collection,
  addDoc,
  getDoc,
  getDocs,
  doc,
  setDoc,
   deleteDoc 
} from "firebase/firestore";
import { firestore } from "../../../../backend/firebaseconfig"; 

/**
 * Saves or updates Cashflow Modeller data for a specific user in Firestore.
 *
 * If an existing document ID is provided, updates the corresponding document with the new data.
 * Otherwise, creates a new document with the provided data and returns its ID.
 *
 * @async
 * @function
 * @param {string} userRole - The role of the user (e.g., 'staff', 'advisor').
 * @param {string} userEmail - The email address of the user.
 * @param {Object} data - The cashflow modeller data to be saved.
 * @param {string|null} [existingDocId=null] - The ID of the existing document to update, or null to create a new document.
 * @returns {Promise<string>} The ID of the saved or updated document.
 */
export const saveCashflowModellerData = async (
  userRole,
  userEmail,
  data,
  existingDocId = null
) => {
  const refPath = `Authorized Members/${userRole}/Data/${userEmail}/Cashflow Modeller`;
  const currentAdviserEmail = localStorage.getItem("currentAdviserEmail");

  const now = new Date().toISOString();
  const payload = { ...data, updatedOn: now, forAdviser: currentAdviserEmail };

  if (existingDocId) {
    const docRef = doc(firestore, `${refPath}/${existingDocId}`);

    await setDoc(docRef, payload,  { merge: true }); //  Update
    return existingDocId;
  } else {
    const collectionRef = collection(firestore, refPath);
    const docRef = await addDoc(collectionRef, {
      ...payload,
      createdAt: now,
      currentAdviserEmail,
    });
    return docRef.id; // Return new doc ID
  }
};

/**
 * Retrieves cashflow modeller data for a specific user and document ID from Firestore.
 *
 * @async
 * @function
 * @param {string} userRole - The role of the user (e.g., 'admin', 'advisor').
 * @param {string} userEmail - The email address of the user.
 * @param {string} docId - The document ID of the cashflow modeller session.
 * @returns {Promise<Object|null>} The data object if found, or null if not found or on error.
 */
export const getCashflowModellerData = async (userRole, userEmail, docId) => {
  // console.log("Fetching cashflow modeller data:", { userRole, userEmail, docId });
  const ref = doc(
    firestore,
    `Authorized Members/${userRole}/Data/${userEmail}/Cashflow Modeller/${docId}`
  );

  try {
    const docSnap = await getDoc(ref);
   return docSnap.exists() ? { id: docSnap.id, ...docSnap.data() } : null;

  } catch (error) {
    // console.error("Error fetching modeller session:", error);
    return null;
  }
};

/**
 * Retrieves all cashflow entries for a specific user based on their role and email.
 *
 * @async
 * @function
 * @param {string} userRole - The role of the user (e.g., "admin", "member").
 * @param {string} userEmail - The email address of the user.
 * @returns {Promise<Array<Object>>} A promise that resolves to an array of cashflow entry objects.
 * Each entry includes a `uid` property (the document ID) and the rest of the document data.
 * Returns an empty array if an error occurs during fetching.
 */
export const getAllCashflowEntries = async (userRole, userEmail) => {
  const ref = collection(
    firestore,
    `Authorized Members/${userRole}/Data/${userEmail}/Cashflow Modeller`
  );

  try {
    const snapshot = await getDocs(ref);
    const entries = snapshot.docs.map((doc) => ({
      uid: doc.id,
      ...doc.data(),
    }));
    return entries;
  } catch (error) {
    // console.error("Error fetching modeller sessions:", error);
    return [];
  }
};


/**
 * Deletes a cashflow entry document from Firestore for a specific user and role.
 *
 * @async
 * @function deleteCashflowEntry
 * @param {string} userRole - The role of the user (e.g., "admin", "user").
 * @param {string} userEmail - The email address of the user whose entry is to be deleted.
 * @param {string} docId - The document ID of the cashflow entry to delete.
 * @returns {Promise<void>} Resolves when the document is successfully deleted.
 * @throws Will silently fail if an error occurs during deletion.
 */
export const deleteCashflowEntry = async (userRole, userEmail, docId) => {
  try {
    const ref = doc(
      firestore,
      `Authorized Members/${userRole}/Data/${userEmail}/Cashflow Modeller/${docId}`
    );
    await deleteDoc(ref);
    // console.log("Deleted:", docId);
  } catch (error) {
    // console.error("Error deleting entry:", error);
  }
};